import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
// Add this import for react-easy-crop
import Cropper from 'react-easy-crop';
// Your existing lucide-react imports
import { ChevronLeft, ChevronRight, Check, User, UserRoundCheck, Calendar, MapPin, Search, Briefcase, FileText, Upload, BookmarkCheck, Map, X } from 'lucide-react';
// Google maps
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
//Drag n drop
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

//Background
//import miImagen from '/wp-content/uploads/2024/02/sephardiHero2-1900x1020.jpg';

// Componente Google maps
// ADD THIS CONSTANT OUTSIDE THE COMPONENT (at the top of the file, after imports)
const libraries = ['marker', 'places'];

// PlaceSelector Component using @react-google-maps/
const PlaceSelector = ({ 
  formData, 
  updateFormData, 
  placeField, 
  coordsField,
  error
}) => {
  const selectedPlace = formData[placeField] || '';
  const coordsData = formData[coordsField];
  
  // Marker position from form data
  const marker = useMemo(() => {
    if (coordsData) {
      return { 
        lat: coordsData.lat, 
        lng: coordsData.lng 
      };
    }
    return null;
  }, [coordsData]);

  const [mapCenter, setMapCenter] = useState(marker || { lat: 40.7128, lng: -74.0060 });
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [sessionToken, setSessionToken] = useState(null);
  
  const inputRef = useRef(null);
  const suggestionsRef = useRef([]);
  //const autocompleteSessionRef = useRef(null);
  const geocoderRef = useRef(null);
  const mapRef = useRef(null);

  // Load Google Maps API using the library
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: 'AIzaSyBhRcC07wSV95vLN1uGpVAvcL9ob_omw3I',
    libraries: libraries, // Use the constant defined outside
    version: 'weekly' // Use the latest version for new features
  });

  // Initialize services when API is loaded
  useEffect(() => {
    if (isLoaded && window.google) {
      geocoderRef.current = new window.google.maps.Geocoder();
      // Create a new session token for the new Autocomplete API
      setSessionToken(new window.google.maps.places.AutocompleteSessionToken());
    }
  }, [isLoaded]);

  // Update map center when marker changes
  useEffect(() => {
    if (marker) {
      setMapCenter({ lat: marker.lat, lng: marker.lng });
    }
  }, [marker]);

  // Map click handler
  const onMapClick = useCallback((event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    
    // Update coordinates immediately
    updateFormData({
      [coordsField]: { lat, lng }
    });
    
    // Reverse geocode to get address
    if (geocoderRef.current) {
      geocoderRef.current.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK' && results[0]) {
          updateFormData({
            [placeField]: results[0].formatted_address
          });
        }
      });
    }
  }, [updateFormData, placeField, coordsField]);

  // Input change handler - UPDATED FOR NEW API
  const handleInputChange = (e) => {
    const value = e.target.value;
    
    updateFormData({
      [placeField]: value
    });
    
    setSelectedIndex(-1);

    if (value.length > 2 && window.google && window.google.maps && sessionToken) {
      // Use the new AutocompleteService with session token
      const autocompleteService = new window.google.maps.places.AutocompleteService();
      
      autocompleteService.getPlacePredictions(
        { 
          input: value,
          sessionToken: sessionToken
        },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            setSuggestions(predictions);
            setShowSuggestions(true);
          } else {
            setSuggestions([]);
            setShowSuggestions(false);
          }
        }
      );
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Keyboard navigation for suggestions
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          selectSuggestion(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Select suggestion handler - UPDATED FOR NEW API
  const selectSuggestion = useCallback((suggestion) => {
    updateFormData({
      [placeField]: suggestion.description
    });
    
    setShowSuggestions(false);
    setSelectedIndex(-1);

    // Create a new PlacesService instance for getting place details
    if (window.google && window.google.maps && geocoderRef.current) {
      const placesService = new window.google.maps.places.PlacesService(
        document.createElement('div') // Dummy div required by the API
      );
      
      placesService.getDetails(
        {
          placeId: suggestion.place_id,
          fields: ['geometry', 'formatted_address'],
          sessionToken: sessionToken
        },
        (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            
            updateFormData({
              [coordsField]: { lat, lng }
            });
            
            setMapCenter({ lat, lng });
            
            // Update the address with the detailed formatted address
            updateFormData({
              [placeField]: place.formatted_address
            });
            
            // Create a new session token for the next search
            setSessionToken(new window.google.maps.places.AutocompleteSessionToken());
          }
        }
      );
    }
  }, [updateFormData, placeField, coordsField, sessionToken]);

  // Scroll selected suggestion into view
  useEffect(() => {
    if (selectedIndex >= 0 && suggestionsRef.current[selectedIndex]) {
      suggestionsRef.current[selectedIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [selectedIndex]);

  // Handle map load
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  // Handle map unload
  const onMapUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  if (loadError) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-md">
        <p className="text-red-600">Error loading Google Maps. Please try again later.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Search Input */}
      <div className="mb-4 relative">
        {/*<label className="block text-sm font-medium text-gray-700 mb-2">
          Place
        </label>*/}
        <p className="text-sm text-gray-600 mb-2">
          Type in the name of the place you are looking for. If the place does not show up, type the city first, then write the exact address or name, or just move the map and tap the spot you want.
        </p>

        {error && (
          <p style={{ color: '#ef4444', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
            {error}
          </p>
        )}

        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            ref={inputRef}
            type="text"
            value={selectedPlace}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            placeholder="Enter city, country, or address..."
            className={`block w-full pl-10 pr-3 py-2 border ${
              error ? 'border-red-300' : 'border-gray-300'
            } rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
          />
        </div>

        {/* Autocomplete Suggestions */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
            {suggestions.map((suggestion, index) => (
              <div
                key={suggestion.place_id}
                ref={el => suggestionsRef.current[index] = el}
                onClick={() => selectSuggestion(suggestion)}
                className={`px-4 py-2 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                  index === selectedIndex ? 'bg-blue-100' : 'hover:bg-blue-50'
                }`}
              >
                <div className="flex items-start">
                  <MapPin className="h-4 w-4 text-gray-400 mt-1 mr-2 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {suggestion.structured_formatting.main_text}
                    </p>
                    <p className="text-xs text-gray-500">
                      {suggestion.structured_formatting.secondary_text}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Selected Location Info */}
      {marker && (
        <div className="mb-4 p-3 bg-blue-50 rounded-md border border-blue-200">
          <p className="text-sm text-gray-700">
            <span className="font-medium">Coordinates:</span> {marker.lat.toFixed(6)}, {marker.lng.toFixed(6)}
          </p>
        </div>
      )}

      {/* Map Container */}
      <div className="relative">
        <p className="text-sm text-gray-600 mb-2">
          Or click on the map to select a location.  
        </p>
        
        {!isLoaded ? (
          <div className="w-full h-96 rounded-lg border border-gray-300 bg-gray-100 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading map...</p>
            </div>
          </div>
        ) : (
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '400px' }}
            center={mapCenter}
            zoom={marker ? 15 : 12}
            onClick={onMapClick}
            onLoad={onMapLoad}
            onUnmount={onMapUnmount}
            mapId="620dcee3bdbd93e0d1f402ff"
            options={{
              mapTypeControl: false,
              streetViewControl: false  
            }}
          >
            {marker && (
              <Marker
                position={marker}
                animation={window.google.maps.Animation.DROP}
              />
            )}
          </GoogleMap>
        )}
      </div>

      {/* Instructions */}
      <div className="mt-4 p-4 bg-gray-50 rounded-md">
        <h3 className="text-sm font-medium text-gray-900 mb-2">How to use:</h3>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Type in the search box to find a place with autocomplete suggestions</li>
          <li>• Use arrow keys to navigate suggestions and Enter to select</li>
          <li>• Click on the map to select a specific location</li>
          <li>• The selected location will appear in the input field</li>
        </ul>
      </div>
    </div>
  );
};

// Image Upload & Crop Component using react-easy-crop
const ImageUploadStep = ({ formData, updateFormData, nextStep, fields = {
    characterImage: 'characterImage',
    originalImage: 'originalImage',
    cropData: 'cropData'
  },
  onCanProceedChange,
  initialCanProceed = true
}) => {
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const fileInputRef = useRef(null);

  let upimgHeadline = 'Upload a Character Image';
  let upimgPar = 'a portrait image';
  if(fields.originalImage === 'originalImageMP'){
    upimgHeadline = 'Upload a Migration Path Image';
    upimgPar = 'an image that represents this migration path move';
  }

  // Destructure the field names for easier use
  const { characterImage, originalImage, cropData } = fields;
  
  // Use values from parent state
  const imageSrc = formData[originalImage];
  const crop = formData[cropData]?.crop || { x: 0, y: 0 };
  const zoom = formData[cropData]?.zoom || 1;
  const rotation = formData[cropData]?.rotation || 0;

  // Calculate whether user can proceed based on image state
  const canProceed = useMemo(() => {
    // Can proceed if:
    // 1. There's no image at all (user hasn't uploaded anything)
    // 2. OR the image has been saved (characterImage exists)
    return !imageSrc || formData[characterImage];
  }, [imageSrc, formData, characterImage]);

  // Notify parent when canProceed changes
  useEffect(() => {
    if (onCanProceedChange) {
      onCanProceedChange(canProceed);
    }
  }, [canProceed, onCanProceedChange]);




  // Helper function to create cropped image
  const createCroppedImage = async (imageSrc, pixelCrop, rotation = 0) => {
    const image = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    return new Promise((resolve) => {
      image.onload = () => {
        // Calculate canvas size for rotation
        const rotRad = rotation * Math.PI / 180;
        const { width: bBoxWidth, height: bBoxHeight } = {
          width: Math.abs(Math.cos(rotRad) * image.width) + Math.abs(Math.sin(rotRad) * image.height),
          height: Math.abs(Math.sin(rotRad) * image.width) + Math.abs(Math.cos(rotRad) * image.height)
        };

        // Set canvas size to bounding box
        canvas.width = bBoxWidth;
        canvas.height = bBoxHeight;

        // Translate and rotate context
        ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
        ctx.rotate(rotRad);
        ctx.drawImage(image, -image.width / 2, -image.height / 2);

        // Get pixel data of cropped area
        const data = ctx.getImageData(
          pixelCrop.x,
          pixelCrop.y,
          pixelCrop.width,
          pixelCrop.height
        );

        // Set canvas size to final crop
        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;
        ctx.putImageData(data, 0, 0);

        // Convert canvas to Blob
        canvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/jpeg');
      };
      image.src = imageSrc;
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload a JPG, JPEG, or PNG file only.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('File size should be less than 5MB.');
      return;
    }

    setError('');

    const reader = new FileReader();
    reader.onload = (e) => {
      // Save original image to parent state, reset crop data
      updateFormData({
        [originalImage]: e.target.result,
        [cropData]: { crop: { x: 0, y: 0 }, zoom: 1, rotation: 0 },
        [characterImage]: null
      });
    };
    reader.readAsDataURL(file);
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleApplyCrop = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    
    setUploading(true);
    setError('');
    
    try {
      // 1. Create cropped image Blob
      const croppedImageBlob = await createCroppedImage(
        imageSrc,
        croppedAreaPixels,
        rotation
      );
      
      if (!croppedImageBlob) {
        throw new Error('Failed to create cropped image');
      }
      
      // 2. Convert Blob to File for upload
      const croppedImageFile = new File(
        [croppedImageBlob], 
        `character_${Date.now()}.jpg`, 
        { type: 'image/jpeg' }
      );
      
      // 3. Upload to server
      const uploadFormData = new FormData();
      uploadFormData.append('image', croppedImageFile);
      
      //const response = await fetch('http://localhost/sepharad2025/add-character/upload-image.php', {
      const response = await fetch('upload-image.php', {
        method: 'POST',
        body: uploadFormData,
        // Don't set Content-Type header - FormData sets it automatically
      });
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Upload failed');
      }
      
      // 4. Update parent form data with just the filename
      updateFormData({
        [characterImage]: result.filename, // Store only filename
        [originalImage]: imageSrc,
        [cropData]: {
          crop,
          zoom,
          rotation,
          croppedAreaPixels
        }
      });
      
    } catch (err) {
      setError(`Upload failed: ${err.message}`);
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleSkip = () => {
    // Only clear image data if there's actually an image
    if (formData[characterImage] || formData[originalImage]) {
      updateFormData({
        [characterImage]: null,
        [originalImage]: null,
        [cropData]: null
      });
    }
    
    // Always go to the next step
    if (nextStep) {
      nextStep();
    }
  };

  const handleRemove = async () => {
    // If we have a filename stored, we could call a delete endpoint
    // For now, just clear the state
    updateFormData({
      [characterImage]: null,
      [originalImage]: null,
      [cropData]: null
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
    // Removing the image allows proceeding
    if (onCanProceedChange) {
      onCanProceedChange(true);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <Upload style={{ width: '1.5rem', height: '1.5rem', color: '#2563eb' }} />
        <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>{upimgHeadline}</h2>
      </div>

      <div>
        <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1rem' }}>
          Upload {upimgPar} (JPG, PNG, or JPEG, max 5MB). You can crop, zoom, and rotate.
        </p>

        {/* File Upload Area - only show if no image is loaded */}
        {!imageSrc && (
          <div
            style={{
              border: '2px dashed #8ba0bf',
              borderRadius: '0.5rem',
              padding: '2rem',
              textAlign: 'center',
              backgroundColor: '#f9fafb',
              cursor: 'pointer',
              transition: 'border-color 0.15s'
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload style={{ width: '2.5rem', height: '2.5rem', color: '#9ca3af', margin: '0 auto 1rem' }} />
            <p style={{ color: '#6b7280', marginBottom: '0.5rem' }}>
              <span style={{ color: '#2563eb', cursor: 'pointer' }}>Click to upload</span>
            </p>
            <p style={{ color: '#9ca3af', fontSize: '0.75rem' }}>JPG, PNG, or JPEG (max 5MB)</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </div>
        )}

        {/* Cropper - show if image is loaded */}
        {imageSrc && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ position: 'relative', width: '100%', height: '300px', backgroundColor: '#f3f4f6', borderRadius: '0.5rem' }}>
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={3/4}
                cropShape="rect"
                showGrid={true}
                onCropChange={(newCrop) => updateFormData({
                  [cropData]: { ...formData[cropData], crop: newCrop }
                })}
                onZoomChange={(newZoom) => updateFormData({
                  [cropData]: { ...formData[cropData], zoom: newZoom }
                })}
                onRotationChange={(newRotation) => updateFormData({
                  [cropData]: { ...formData[cropData], rotation: newRotation } 
                })}
                onCropComplete={onCropComplete}
              />
            </div>

            {/* Controls */}
            <div style={{ backgroundColor: '#f9fafb', padding: '1rem', borderRadius: '0.5rem' }}>
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                  Zoom: {zoom.toFixed(1)}
                </label>
                <input
                  type="range"
                  value={zoom}
                  min={1}
                  max={3}
                  step={0.1}
                  onChange={(e) => updateFormData({
                    [cropData]: { ...formData[cropData], zoom: parseFloat(e.target.value) }
                  })}
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                  Rotation: {rotation}°
                </label>
                <input
                  type="range"
                  value={rotation}
                  min={0}
                  max={360}
                  step={1}
                  onChange={(e) => updateFormData({
                    [cropData]: { ...formData[cropData], rotation: parseInt(e.target.value) }
                  })}
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={handleApplyCrop}
                disabled={uploading}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: uploading ? '#9ca3af' : '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem',
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                {uploading ? (
                  <>
                    <div style={{
                      width: '1rem',
                      height: '1rem',
                      border: '2px solid white',
                      borderTopColor: 'transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Uploading...
                  </>
                ) : (
                  formData[characterImage] ? 'Update Crop' : 'Apply Crop & Save'
                )}
              </button>
              <button
                onClick={handleRemove}
                disabled={uploading}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: uploading ? '#9ca3af' : '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem'
                }}
              >
                <X style={{ width: '1rem', height: '1rem' }} />
              </button>
            </div>

            {/* Status */}
            {formData[characterImage] && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                <Check style={{ width: '1rem', height: '1rem', color: '#16a34a' }} />
                <p style={{ color: '#16a34a', fontSize: '0.875rem' }}>
                  Image saved. You can edit or remove it.
                </p>
              </div>
            )}
          </div>
        )}

        {error && (
          <p style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.5rem' }}>{error}</p>
        )}
      </div>

      {/* Skip Option */}
      <div style={{ marginTop: '1rem' }}>
        <button
          onClick={handleSkip}
          disabled={uploading}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: 'transparent',
            color: uploading ? '#9ca3af' : '#6b7280',
            border: '1px solid #8ba0bf',
            borderRadius: '0.375rem',
            cursor: uploading ? 'not-allowed' : 'pointer',
            fontSize: '0.875rem'
          }}
        >
          {formData[characterImage] || formData[originalImage] 
            ? 'Remove Image & Continue' 
            : 'Skip this step (no image)'}
        </button>
        <p style={{ color: '#9ca3af', fontSize: '0.75rem', marginTop: '0.5rem' }}>
          {formData[characterImage] 
            ? 'This will remove the uploaded image entirely' 
            : 'You can add an image later if you don\'t have one now.'}
        </p>
      </div>
    </div>
  );
};

// Main Parent Component
const CharacterRegistration = () => {
  // STATE MANAGEMENT
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    characterImage: null,      // Final cropped image
    originalImage: null,       // Original uploaded image (base64)
    cropData: null,           // Crop state {crop, zoom, rotation}
    firstName: '',
    lastName: '',
    akas: '',
    birthYear: '',
    birthDecade: '',
    birthPlace: '',
    birthPlaceCoords: null,
    isAlive: '',
    deathYear: '',
    deathDecade: '',
    deathPlace: '',
    deathPlaceCoords: null,
    profession: '',
    classification: '',
    frmtitle: '',
    frmdesc: '',
    characterImageMP: null,      // Final cropped image
    originalImageMP: null,       // Original uploaded image (base64)
    cropDataMP: null,           // Crop state {crop, zoom, rotation}
    mpPlaceFrom: '',
    mpPlaceFromCoords: null,
    mpPlaceTo: '',
    mpPlaceToCoords: null,
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Predefined profession options
  const professionOptions = [
    'Doctor',
    'Engineer',
    'Educator',
    'Artist',
    'Writer',
    'Musician',
    'Scientist',
    'Lawyer',
    'Business Owner',
    'Thinker',
    'Rabbi',
    'Intelectual',
    'Academic',
    'Community Leader',
    'Other (specify below)'
  ];

  // Predefined classification options
  const classificationOptions = [
    'Iberian Sephardi',
    'Western Sephardi',
    'Eastern Sephardi',
    'Mizrahi Sephardi',
    'North African Sephardi',
    'Crypto-Sephardi (Converso Legacy)',
    'Other (specify below)'
  ];

  // FIXED: Update the updateFormData function to handle object updates
  const updateFormData = useCallback((updates) => {
    setFormData(prev => ({
      ...prev,
      ...updates
    }));
    
    // Clear errors for updated fields
    const updatedFields = Object.keys(updates);
    const newErrors = { ...errors };
    updatedFields.forEach(field => {
      if (newErrors[field]) {
        delete newErrors[field];
      }
    });
    setErrors(newErrors);
  }, [errors]);

  // EL MODAL
  const ElModal = () => {
    const handleClick = (e) => {
      e.preventDefault();
      document.querySelector("dialog").showModal();
    };

    return (
      <a href="#1" className="herelink" onClick={handleClick}>here</a>
    );
  } 

  // Update the modal effect to use step 7 (classification) instead of 8
  useEffect(() => {
    if (step === 7) {
      const dialog = document.querySelector("dialog");
      const button = document.querySelector("dialog button");
      if (dialog && button) {
        button.addEventListener("click", () => {
          dialog.close();
        });
      }
    }
  }, [step]); // Add step as dependency to re-run when step changes

// Go to the top of the page after changing steps
  useEffect(() => {
    // Scroll to top when step changes
    window.scrollTo({
      top: 0,
      behavior: 'smooth' // Use 'auto' for instant scroll
    });
  }, [step]); // This runs every time step changes

  // VALIDATION
  const validateStep = () => {
    const newErrors = {};

    switch (step) {
      case 1: // Character Name
        if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
        if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
        break;
      case 2: // Birth Year/Decade
        if (!formData.birthYear && !formData.birthDecade) {
          newErrors.birthYear = 'Please specify either a year or decade';
        }
        if (formData.birthYear && (isNaN(formData.birthYear) || formData.birthYear.length !== 4)) {
          newErrors.birthYear = 'Please enter a valid 4-digit year';
        }
        break;
      case 3: // Birth Place
        if (!formData.birthPlace.trim()) newErrors.birthPlace = 'Birth place is required';
        if (!formData.birthPlaceCoords) newErrors.birthPlace = 'Birth place is required';
        break;
      case 4: // Last known year/Decade
        if (!formData.isAlive) {
          newErrors.isAlive = 'Please specify whether this character is alive';
        }

        if (formData.isAlive === 'yes') {
          // Alive characters should not require a death year/decade.
          // We store 0000 as the hidden deathYear value.
          break;
        }

        if (formData.isAlive === 'no') {
          if (!formData.deathYear && !formData.deathDecade) {
            newErrors.deathYear = 'Please specify either a year or decade';
          }

          if (
            formData.deathYear &&
            (
              isNaN(formData.deathYear) ||
              formData.deathYear.length !== 4 ||
              formData.deathYear === '0000'
            )
          ) {
            newErrors.deathYear = 'Please enter a valid 4-digit year';
          }
        }
        break;
      case 5: // Last known place
        if (!formData.deathPlace.trim()) newErrors.deathPlace = 'Last known place is required';
        if (!formData.deathPlaceCoords) newErrors.deathPlace = 'Last known place is required';
        break;
      case 6: // Most known for (profession)
        if (!formData.profession.trim()) newErrors.profession = 'Most known for is required';
        break;
      case 7: // Classification
        if (!formData.classification.trim()) newErrors.classification = 'Denomination is required';
        break;
      case 8: // Image Upload (optional, no validation needed)
        break;
      case 11: // Character Name
        if (!formData.frmtitle.trim()) newErrors.frmtitle = 'Title is required';
        if (!formData.frmdesc.trim()) newErrors.frmdesc = 'Description is required';
        break;
      case 13: // mp place From
        if (!formData.mpPlaceFrom.trim()) newErrors.mpPlaceFrom = 'The first recorded move place is required';
        if (!formData.mpPlaceFromCoords) newErrors.mpPlaceFrom = 'The first recorded move place is required';
        break;
      case 14: // mp place From
        if (!formData.mpPlaceTo.trim()) newErrors.mpPlaceTo = 'This place is required';
        if (!formData.mpPlaceToCoords) newErrors.mpPlaceTo = 'This place is required';
        break;
      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
    
  };

    // Begin migration path TEMPORAL xxxxxxxxxxxxxxxxxxxx
  const beginMigrationPath = () => {
    setStep(11);
  };

  // NAVIGATION
  const nextStep = () => {
    if (!validateStep()) {
      // Scroll to top when there's a validation error
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      return;
    }

    setStep(prev => prev + 1);
  };

  const prevStep = () => {
    setStep(prev => prev - 1);
  };

  const goToStep = (targetStep) => setStep(targetStep);

  // FIRST FORM SUBMISSION
  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      // Prepare submission data - now characterImage is just a filename string
      const submissionData = { ...formData };

      if (submissionData.isAlive === 'yes') {
        submissionData.deathYear = '0000';
        submissionData.deathDecade = '';
      }

      // Convert coordinates to strings if needed
      if (formData.birthPlaceCoords) {
        submissionData.birthPlaceCoords = JSON.stringify(formData.birthPlaceCoords);
      }
      if (formData.deathPlaceCoords) {
        submissionData.deathPlaceCoords = JSON.stringify(formData.deathPlaceCoords);
      }

      //const response = await fetch('http://localhost/sepharad2025/add-character/save-character.php', {
      const response = await fetch('save-character.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData)
      });

      const result = await response.json();

      if (result.success) {
        //alert('Character saved successfully! ' + result.popochas);
        // Reset form
        /*
        setFormData({
          characterImage: null,
          originalImage: null,
          cropData: null,
          firstName: '',
          lastName: '',
          birthYear: '',
          birthDecade: '',
          birthPlace: '',
          birthPlaceCoords: null,
          deathYear: '',
          deathDecade: '',
          deathPlace: '',
          deathPlaceCoords: null,
          profession: '',
          classification: '',
          frmtitle: '',
          frmdesc: '',
          mpPlaceFrom: '',
          mpPlaceFromCoords: null,
          mpPlaceTo: '',
          mpPlaceToCoords: null,
        });
        */
       setStep(10);
      } else {
        alert('Error saving character: ' + result.message);
      }
    } catch (error) {
      alert('Error submitting form: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // SECOND FORM SUBMISSION
  const [ordering, setOrdering] = useState('First');

  const [chNom, setChNom] = useState('');

  const handleSubmitFRM = async () => {

    if (validateStep()){
      setIsSubmitting(true);

      try {
        // Prepare submission data - now characterImage is just a filename string
        const submissionData = { ...formData };
        
        // Convert coordinates to strings if needed
        if (formData.mpPlaceFromCoords) {
          submissionData.mpPlaceFromCoords = JSON.stringify(formData.mpPlaceFromCoords);
        }
        if (formData.mpPlaceToCoords) {
          submissionData.mpPlaceToCoords = JSON.stringify(formData.mpPlaceToCoords);
        }

        //const response = await fetch('http://localhost/sepharad2025/add-character/save-character.php', {
        const response = await fetch('save-character.php', {
          credentials: 'same-origin',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(submissionData)
        });

        const result = await response.json();

        if (result.success) {
          //alert('Character saved successfully! ' + result.popochas);
          // Reset form
          
          setFormData({
            /*
            characterImage: null,
            originalImage: null,
            cropData: null,
            firstName: '',
            lastName: '',
            birthYear: '',
            birthDecade: '',
            birthPlace: '',
            birthPlaceCoords: null,
            deathYear: '',
            deathDecade: '',
            deathPlace: '',
            deathPlaceCoords: null,
            profession: '',
            classification: '',
            */
            frmtitle: '',
            frmdesc: '',
            characterImageMP: null,      // Final cropped image
            originalImageMP: null,       // Original uploaded image (base64)
            cropDataMP: null,           // Crop state {crop, zoom, rotation}
            mpPlaceFrom: '',
            mpPlaceFromCoords: null,
            mpPlaceTo: '',
            mpPlaceToCoords: null,
          });
          setChNom(result.charactername);
          setOrdering(result.ordering);
          setStep(15);
        } else {
          alert('Error saving character: ' + result.message);
        }
      } catch (error) {
        alert('Error submitting form: ' + error.message);
      } finally {
        setIsSubmitting(false);
      }
    }else{
      // Scroll to top where the missing field/error is
      window.scrollTo({
        top: 0,
        behavior: 'smooth' 
      });
    }
  };

  // GET MPs of added user
  const [phpData, setPhpData] = useState([]);
  const [chName, setChName] = useState('');
  const [loadingPhpData, setLoadingPhpData] = useState(false);
  const [phpError, setPhpError] = useState(null);

  const fetchPhpData = async () => {
    setLoadingPhpData(true);
    setPhpError(null);
    setStep(16);
    
    try {
      //const response = await fetch('http://localhost/sepharad2025/add-character/get-move-data.php', {
      const response = await fetch('get-move-data.php', {
        method: 'GET',
        credentials: 'same-origin' // IMPORTANT for PHP sessions
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error('PHP returned an error');
      }

      setPhpData(result.data);

      setChName(result.charactername);
    } catch (err) {
      setPhpError(err.message);
    } finally {
      setLoadingPhpData(false);
    }
  };

  // Moves and Drag n drop order change

  const [moves, setMoves] = useState([]);

  useEffect(() => {
    if (phpData.length > 0) {
      setMoves(phpData);
    }
  }, [phpData]);

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(moves);
    const [movedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, movedItem);

    setMoves(items);

    // Persist new order
    saveNewOrder(items);
  };
  
  const saveNewOrder = async (items) => {
    const payload = items.map((item, index) => ({
      id: item.id,
      orden: index + 1
    }));
    //await fetch("http://localhost/sepharad2025/add-character/updateMigrationOrder.php", {
    await fetch("updateMigrationOrder.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });
  };

  // links
  const openInNewTab = (url) => {
    // For security, use noopener and noreferrer
    const newWindow = window.open(url, '_top', 'noopener,noreferrer');
    if (newWindow) {
      newWindow.opener = null; // Further security enhancement
    }
  };

  const openNewChar = () => {
    openInNewTab('/addchar/');
  };

  const openFinsh = () => {
    openInNewTab('/');
  };

  // images to be uploaded state
  const [canProceedFromImageStep, setCanProceedFromImageStep] = useState(true);
  const [canProceedFromImageStepMP, setCanProceedFromImageStepMP] = useState(true);

  const canGoNext = () => {
    if (step === 4) {
      if (!formData.isAlive) {
        return false;
      }

      if (formData.isAlive === 'yes') {
        return true;
      }

      if (formData.isAlive === 'no') {
        return Boolean(formData.deathYear || formData.deathDecade);
      }
    }    
    if (step === 8) {
      // For regular image step
      return canProceedFromImageStep;
    }
    if (step === 12) {
      // For MP image step
      return canProceedFromImageStepMP;
    }
    return true; // Other steps are always enabled
  };


  // User logged in?
  const [wpUsername, setWpUsername] = useState(null);
  const [loadingWp, setLoadingWp] = useState(true);
  useEffect(() => {
    fetch("check-wp-session.php", {
      credentials: "include"
    })
      .then(res => res.json())
      .then(data => {
        setWpUsername(data.username ?? null);
      })
      .finally(() => setLoadingWp(false));
  }, []);

  if (loadingWp) {
    return <p>Loading session…</p>;
  }

  
  // RENDER STEPS - Updated order
  const renderStep = () => {
    switch (step) {
      case 1: // Character Name
      
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <User style={{ width: '1.5rem', height: '1.5rem', color: '#2563eb' }} />
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>Character Name</h2>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '1.1rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                First Name * 
              </label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => updateFormData({ firstName: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.5rem 1rem',
                  border: errors.firstName ? '1px solid #ef4444' : '1px solid #8ba0bf',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'border-color 0.15s'
                }}
                placeholder="Enter first name"
                onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                onBlur={(e) => e.target.style.borderColor = errors.firstName ? '#ef4444' : '#8ba0bf'}
              />
              {errors.firstName && (
                <p style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem' }}>{errors.firstName}</p>
              )}
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '1.1rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                Last Name *
              </label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => updateFormData({ lastName: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.5rem 1rem',
                  border: errors.lastName ? '1px solid #ef4444' : '1px solid #8ba0bf',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  outline: 'none'
                }}
                placeholder="Enter last name"
                onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                onBlur={(e) => e.target.style.borderColor = errors.lastName ? '#ef4444' : '#8ba0bf'}
              />
              {errors.lastName && (
                <p style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem' }}>{errors.lastName}</p>
              )}
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '1.1rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                Also known as
              </label>
              <input
                type="text"
                value={formData.akas}
                onChange={(e) => updateFormData({ akas: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.5rem 1rem',
                  border: '1px solid #8ba0bf',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'border-color 0.15s'
                }}
                placeholder="Enter other name(s)"
                onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                onBlur={(e) => e.target.style.borderColor = '#8ba0bf'}
              />
            </div>

          </div>
        );

      case 2: // Birth Year/Decade
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <Calendar style={{ width: '1.5rem', height: '1.5rem', color: '#2563eb' }} />
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>Birth Year/Decade</h2>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '1.1rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                Birth Year
              </label>
              <input
                type="text"
                value={formData.birthYear}
                onChange={(e) => {
                  updateFormData({ 
                    birthYear: e.target.value,
                    birthDecade: e.target.value ? '' : formData.birthDecade
                  });
                }}
                style={{
                  width: '100%',
                  padding: '0.5rem 1rem',
                  border: errors.birthYear ? '1px solid #ef4444' : '1px solid #8ba0bf',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  outline: 'none'
                }}
                placeholder="e.g., 1985"
                maxLength="4"
                onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                onBlur={(e) => e.target.style.borderColor = errors.birthYear ? '#ef4444' : '#8ba0bf'}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{ flex: 1, borderTop: '1px solid #8ba0bf' }}></div>
              <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>OR</span>
              <div style={{ flex: 1, borderTop: '1px solid #8ba0bf' }}></div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '1.1rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                Birth Decade
              </label>
              <select
                value={formData.birthDecade}
                onChange={(e) => {
                  updateFormData({ 
                    birthDecade: e.target.value,
                    birthYear: e.target.value ? '' : formData.birthYear
                  });
                }}
                style={{
                  width: '100%',
                  padding: '0.5rem 1rem',
                  border: '1px solid #8ba0bf',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  outline: 'none',
                }}
                onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                onBlur={(e) => e.target.style.borderColor = '#8ba0bf'}
              >
                <option value="">Select a decade</option>
                <option value="1300s">1300s</option>
                <option value="1310s">1310s</option>
                <option value="1320s">1320s</option>
                <option value="1330s">1330s</option>
                <option value="1340s">1340s</option>
                <option value="1350s">1350s</option>
                <option value="1360s">1360s</option>
                <option value="1370s">1370s</option>
                <option value="1380s">1380s</option>
                <option value="1390s">1390s</option>
                <option value="1400s">1400s</option>
                <option value="1410s">1410s</option>
                <option value="1420s">1420s</option>
                <option value="1430s">1430s</option>
                <option value="1440s">1440s</option>
                <option value="1450s">1450s</option>
                <option value="1460s">1460s</option>
                <option value="1470s">1470s</option>
                <option value="1480s">1480s</option>
                <option value="1490s">1490s</option>
                <option value="1500s">1500s</option>
                <option value="1510s">1510s</option>
                <option value="1520s">1520s</option>
                <option value="1530s">1530s</option>
                <option value="1540s">1540s</option>
                <option value="1550s">1550s</option>
                <option value="1560s">1560s</option>
                <option value="1570s">1570s</option>
                <option value="1580s">1580s</option>
                <option value="1590s">1590s</option>
                <option value="1600s">1600s</option>
                <option value="1610s">1610s</option>
                <option value="1620s">1620s</option>
                <option value="1630s">1630s</option>
                <option value="1640s">1640s</option>
                <option value="1650s">1650s</option>
                <option value="1660s">1660s</option>
                <option value="1670s">1670s</option>
                <option value="1680s">1680s</option>
                <option value="1690s">1690s</option>
                <option value="1700s">1700s</option>
                <option value="1710s">1710s</option>
                <option value="1720s">1720s</option>
                <option value="1730s">1730s</option>
                <option value="1740s">1740s</option>
                <option value="1750s">1750s</option>
                <option value="1760s">1760s</option>
                <option value="1770s">1770s</option>
                <option value="1780s">1780s</option>
                <option value="1790s">1790s</option>
                <option value="1800s">1800s</option>
                <option value="1810s">1810s</option>
                <option value="1820s">1820s</option>
                <option value="1830s">1830s</option>
                <option value="1840s">1840s</option>
                <option value="1850s">1850s</option>
                <option value="1860s">1860s</option>
                <option value="1870s">1870s</option>
                <option value="1880s">1880s</option>
                <option value="1890s">1890s</option>
                <option value="1900s">1900s</option>
                <option value="1910s">1910s</option>
                <option value="1920s">1920s</option>
                <option value="1930s">1930s</option>
                <option value="1940s">1940s</option>
                <option value="1950s">1950s</option>
                <option value="1960s">1960s</option>
                <option value="1970s">1970s</option>
                <option value="1980s">1980s</option>
                <option value="1990s">1990s</option>
                <option value="2000s">2000s</option>
                <option value="2010s">2010s</option>
                <option value="2020s">2020s</option>
              </select>
              {errors.birthYear && (
                <p style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem' }}>{errors.birthYear}</p>
              )}
            </div>
          </div>
        );

      case 3: // Birth Place
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <MapPin style={{ width: '1.5rem', height: '1.5rem', color: '#2563eb' }} />
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>Birth Place</h2>
            </div>

            <PlaceSelector 
              formData={formData}
              updateFormData={updateFormData}
              placeField="birthPlace"
              coordsField="birthPlaceCoords"
              error={errors.birthPlace} 
            />
          </div>
        );
      case 4: // Alive / Last known year/Decade
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <Calendar style={{ width: '1.5rem', height: '1.5rem', color: '#2563eb' }} />
        <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>
          Last known year
        </h2>
      </div>

      <div>
        <label style={{ display: 'block', fontSize: '1.1rem', fontWeight: '500', color: '#374151', marginBottom: '0.75rem' }}>
          Is this character alive? *
        </label>

        {errors.isAlive && (
          <p style={{ color: '#ef4444', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
            {errors.isAlive}
          </p>
        )}

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => {
              updateFormData({
                isAlive: 'yes',
                deathYear: '0000',
                deathDecade: ''
              });
            }}
            style={{
              padding: '0.5rem 1rem',
              border: formData.isAlive === 'yes' ? '1px solid #2563eb' : '1px solid #8ba0bf',
              borderRadius: '0.5rem',
              backgroundColor: formData.isAlive === 'yes' ? '#2563eb' : 'white',
              color: formData.isAlive === 'yes' ? 'white' : '#374151',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500'
            }}
          >
            Yes, this character is alive
          </button>

          <button
            type="button"
            onClick={() => {
              updateFormData({
                isAlive: 'no',
                deathYear: formData.deathYear === '0000' ? '' : formData.deathYear,
                deathDecade: formData.deathDecade
              });
            }}
            style={{
              padding: '0.5rem 1rem',
              border: formData.isAlive === 'no' ? '1px solid #2563eb' : '1px solid #8ba0bf',
              borderRadius: '0.5rem',
              backgroundColor: formData.isAlive === 'no' ? '#2563eb' : 'white',
              color: formData.isAlive === 'no' ? 'white' : '#374151',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500'
            }}
          >
            No
          </button>
        </div>

        {formData.isAlive === 'yes' && (
          <div style={{
            marginTop: '1rem',
            padding: '1rem',
            backgroundColor: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: '0.5rem'
          }}>
            <p style={{ fontSize: '0.875rem', color: '#1e40af', margin: 0 }}>
              This character will be saved as alive.
            </p>
          </div>
        )}
      </div>

      {formData.isAlive === 'no' && (
        <>
          <div>
            <label style={{ display: 'block', fontSize: '1.1rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
              Last known year
            </label>
            <input
              type="text"
              value={formData.deathYear}
              onChange={(e) => {
                updateFormData({
                  deathYear: e.target.value,
                  deathDecade: e.target.value ? '' : formData.deathDecade
                });
              }}
              style={{
                width: '100%',
                padding: '0.5rem 1rem',
                border: errors.deathYear ? '1px solid #ef4444' : '1px solid #8ba0bf',
                borderRadius: '0.5rem',
                fontSize: '1rem',
                outline: 'none'
              }}
              placeholder="e.g., 1985"
              maxLength="4"
              onFocus={(e) => e.target.style.borderColor = '#2563eb'}
              onBlur={(e) => e.target.style.borderColor = errors.deathYear ? '#ef4444' : '#8ba0bf'}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ flex: 1, borderTop: '1px solid #8ba0bf' }}></div>
            <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>OR</span>
            <div style={{ flex: 1, borderTop: '1px solid #8ba0bf' }}></div>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '1.1rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
              Last known Decade
            </label>
            <select
              value={formData.deathDecade}
              onChange={(e) => {
                updateFormData({
                  deathDecade: e.target.value,
                  deathYear: e.target.value ? '' : formData.deathYear
                });
              }}
              style={{
                width: '100%',
                padding: '0.5rem 1rem',
                border: '1px solid #8ba0bf',
                borderRadius: '0.5rem',
                fontSize: '1rem',
                outline: 'none',
              }}
              onFocus={(e) => e.target.style.borderColor = '#2563eb'}
              onBlur={(e) => e.target.style.borderColor = '#8ba0bf'}
            >
              <option value="">Select a decade</option>
              <option value="1300s">1300s</option>
              <option value="1310s">1310s</option>
              <option value="1320s">1320s</option>
              <option value="1330s">1330s</option>
              <option value="1340s">1340s</option>
              <option value="1350s">1350s</option>
              <option value="1360s">1360s</option>
              <option value="1370s">1370s</option>
              <option value="1380s">1380s</option>
              <option value="1390s">1390s</option>
              <option value="1400s">1400s</option>
              <option value="1410s">1410s</option>
              <option value="1420s">1420s</option>
              <option value="1430s">1430s</option>
              <option value="1440s">1440s</option>
              <option value="1450s">1450s</option>
              <option value="1460s">1460s</option>
              <option value="1470s">1470s</option>
              <option value="1480s">1480s</option>
              <option value="1490s">1490s</option>
              <option value="1500s">1500s</option>
              <option value="1510s">1510s</option>
              <option value="1520s">1520s</option>
              <option value="1530s">1530s</option>
              <option value="1540s">1540s</option>
              <option value="1550s">1550s</option>
              <option value="1560s">1560s</option>
              <option value="1570s">1570s</option>
              <option value="1580s">1580s</option>
              <option value="1590s">1590s</option>
              <option value="1600s">1600s</option>
              <option value="1610s">1610s</option>
              <option value="1620s">1620s</option>
              <option value="1630s">1630s</option>
              <option value="1640s">1640s</option>
              <option value="1650s">1650s</option>
              <option value="1660s">1660s</option>
              <option value="1670s">1670s</option>
              <option value="1680s">1680s</option>
              <option value="1690s">1690s</option>
              <option value="1700s">1700s</option>
              <option value="1710s">1710s</option>
              <option value="1720s">1720s</option>
              <option value="1730s">1730s</option>
              <option value="1740s">1740s</option>
              <option value="1750s">1750s</option>
              <option value="1760s">1760s</option>
              <option value="1770s">1770s</option>
              <option value="1780s">1780s</option>
              <option value="1790s">1790s</option>
              <option value="1800s">1800s</option>
              <option value="1810s">1810s</option>
              <option value="1820s">1820s</option>
              <option value="1830s">1830s</option>
              <option value="1840s">1840s</option>
              <option value="1850s">1850s</option>
              <option value="1860s">1860s</option>
              <option value="1870s">1870s</option>
              <option value="1880s">1880s</option>
              <option value="1890s">1890s</option>
              <option value="1900s">1900s</option>
              <option value="1910s">1910s</option>
              <option value="1920s">1920s</option>
              <option value="1930s">1930s</option>
              <option value="1940s">1940s</option>
              <option value="1950s">1950s</option>
              <option value="1960s">1960s</option>
              <option value="1970s">1970s</option>
              <option value="1980s">1980s</option>
              <option value="1990s">1990s</option>
              <option value="2000s">2000s</option>
              <option value="2010s">2010s</option>
              <option value="2020s">2020s</option>
            </select>

            {errors.deathYear && (
              <p style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                {errors.deathYear}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );

      case 5: // Last known place
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <MapPin style={{ width: '1.5rem', height: '1.5rem', color: '#2563eb' }} />
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>{formData.isAlive === 'yes' ? 'Current or Last Known Place' : 'Last known place'}</h2>
            </div>

            <PlaceSelector 
              formData={formData}
              updateFormData={updateFormData}
              placeField="deathPlace"
              coordsField="deathPlaceCoords"
              error={errors.deathPlace} 
            />
          </div>
        );

      case 6: // Most known for (profession)
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <Briefcase style={{ width: '1.5rem', height: '1.5rem', color: '#2563eb' }} />
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>Most known for</h2>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '1.1rem', fontWeight: '500', color: '#374151', marginBottom: '0.75rem' }}>
                Select or enter what this character is most know for*
              </label>
              <p className="text-sm text-gray-600 mb-2" style={{ display: 'block', fontSize: '0.875rem', fontWeight: '300', color: '#374151', marginBottom: '2rem' }}>
                Please write this person's main role or contribution, for example rabbi and educator, community leader, Latino language activist, musician, engineer, writer, lawyer, business owner, intellectual, or any other role that fits the character.
              </p>
              
              {/*Cambiar display none a grid si queremos mostrar las opciones*/}
              <div style={{ display: 'none', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
                {professionOptions.map((prof) => (
                  <button
                    key={prof}
                    type="button"
                    onClick={() => updateFormData({ profession: prof === 'Other (specify below)' ? '' : prof })}
                    style={{
                      padding: '0.5rem 1rem',
                      border: formData.profession === prof ? '1px solid #2563eb' : '1px solid #8ba0bf',
                      borderRadius: '0.5rem',
                      textAlign: 'left',
                      backgroundColor: formData.profession === prof ? '#2563eb' : 'white',
                      color: formData.profession === prof ? 'white' : '#374151',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      fontSize: '0.875rem'
                    }}
                    onMouseEnter={(e) => {
                      if (formData.profession !== prof) {
                        e.target.style.borderColor = '#60a5fa';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (formData.profession !== prof) {
                        e.target.style.borderColor = '#8ba0bf';
                      }
                    }}
                  >
                    {prof}
                  </button>
                ))}
              </div>
              {/*Or type a custom profession...*/}
              <input
                type="text"
                value={formData.profession}
                onChange={(e) => updateFormData({ profession: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.5rem 1rem',
                  border: errors.profession ? '1px solid #ef4444' : '1px solid #8ba0bf',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  outline: 'none'
                }}
                placeholder="Enter the character's main role or contribution"
                onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                onBlur={(e) => e.target.style.borderColor = errors.profession ? '#ef4444' : '#8ba0bf'}
              />
              {errors.profession && (
                <p style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem' }}>{errors.profession}</p>
              )}
            </div>
          </div>
        );

      case 7: // Classification
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <Briefcase style={{ width: '1.5rem', height: '1.5rem', color: '#2563eb' }} />
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>Classification</h2>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '1.1rem', fontWeight: '500', color: '#374151', marginBottom: '0.75rem' }}>
                Select or enter the person's denomination. Find more about each denomination <ElModal />.*
              </label>
              <dialog className="fixed inset-y-0 right-0 h-full">
                <button autoFocus>x</button>
                <h2>Which denomination should I choose for this character?</h2>
                <p><strong>Iberian Sephardi</strong>: Families with ancestral origins in Spain or Portugal, including diasporic resettlement in North Africa, Italy, Western Europe (Amsterdam, France, England), or Eastern Europe (Balkan, Bulgaria, and generally the Ottoman Empire after 1492).</p>
                <p><strong>Western Sephardi</strong>: Post-expulsion Sephardim who migrated to Amsterdam, Hamburg, London, and the Caribbean, maintaining a Spanish-Portuguese identity and merchant networks.</p>
                <p><strong>Eastern Sephardi</strong>: Descendants of Iberian exiles settled in the Ottoman Empire (Salonica, Istanbul, Izmir, Sofia, Balkans). Ladino-speaking and integrated in Ottoman society.</p>
                <p><strong>Mizrahi Sephardi</strong>: Jews from Arab or Persian lands (e.g., Iraq, Syria, Egypt, Iran) who follow Sephardi liturgy or identify with the broader Sephardi label.</p>
                <p><strong>North African Sephardi</strong>: Jews from Morocco, Algeria, Tunisia, and Libya, blending pre-expulsion traditions with post-1492 Iberian Sephardi influences.</p>
                <p><strong>Crypto-Sephardi (Converso Legacy)</strong>: Refers to individuals or families descending from Jews who converted under duress during the Iberian Inquisition (Conversos or Anusim), especially those with migration stories tied to the Americas—Mexico, New Mexico, Brazil, or the Caribbean. This category acknowledges hidden, reclaimed, or re-emerging Jewish identities.</p>
                <p><strong>Other Sephardi</strong>: For families or individuals not fitting neatly into the mentioned categories, including mixed heritage, unknown or ambiguous origins.</p>
              </dialog>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
                {classificationOptions.map((prof) => (
                  <button
                    key={prof}
                    type="button"
                    onClick={() => updateFormData({ classification: prof === 'Other (specify below)' ? '' : prof })}
                    style={{
                      padding: '0.5rem 1rem',
                      border: formData.classification === prof ? '1px solid #2563eb' : '1px solid #8ba0bf',
                      borderRadius: '0.5rem',
                      textAlign: 'left',
                      backgroundColor: formData.classification === prof ? '#2563eb' : 'white',
                      color: formData.classification === prof ? 'white' : '#374151',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      fontSize: '0.875rem'
                    }}
                    onMouseEnter={(e) => {
                      if (formData.classification !== prof) {
                        e.target.style.borderColor = '#60a5fa';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (formData.classification !== prof) {
                        e.target.style.borderColor = '#8ba0bf';
                      }
                    }}
                  >
                    {prof}
                  </button>
                ))}
              </div>

              <input
                type="text"
                value={formData.classification}
                onChange={(e) => updateFormData({ classification: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.5rem 1rem',
                  border: errors.classification ? '1px solid #ef4444' : '1px solid #8ba0bf',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  outline: 'none'
                }}
                placeholder="Or type a custom denomination..."
                onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                onBlur={(e) => e.target.style.borderColor = errors.classification ? '#ef4444' : '#8ba0bf'}
              />
              {errors.classification && (
                <p style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem' }}>{errors.classification}</p>
              )}
            </div>
          </div>
        );

      case 8: // Image Upload Character
        return <ImageUploadStep formData={formData} updateFormData={updateFormData} nextStep={nextStep} onCanProceedChange={setCanProceedFromImageStep} />;

      case 9: // Review and Submit Information
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <FileText style={{ width: '1.5rem', height: '1.5rem', color: '#2563eb' }} />
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>Review and Submit Information</h2>
            </div>

            <div style={{ backgroundColor: '#f9fafb', borderRadius: '0.5rem', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', borderBottom: '1px solid #e5e7eb', paddingBottom: '0.75rem' }}>
                <div>
                  <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0 0 0.25rem 0' }}>Name</p>
                  <p style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                    {formData.firstName} {formData.lastName} 
                    {formData.akas && (
                      <>
                        <br />AKA: 
                        {formData.akas}
                      </>
                    )}
                  </p>
                </div>
                <button
                  onClick={() => goToStep(1)} // Updated from 2 to 1
                  style={{
                    color: '#2563eb',
                    fontSize: '0.875rem',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                >
                  Edit
                </button>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', borderBottom: '1px solid #e5e7eb', paddingBottom: '0.75rem' }}>
                <div>
                  <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0 0 0.25rem 0' }}>Birth Year/Decade</p>
                  <p style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                    {formData.birthYear || formData.birthDecade}
                  </p>
                </div>
                <button
                  onClick={() => goToStep(2)} // Updated from 3 to 2
                  style={{
                    color: '#2563eb',
                    fontSize: '0.875rem',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                >
                  Edit
                </button>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', borderBottom: '1px solid #e5e7eb', paddingBottom: '0.75rem' }}>
                <div>
                  <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0 0 0.25rem 0' }}>Birth Place</p>
                  <p style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                    {formData.birthPlace}
                  </p>
                </div>
                <button
                  onClick={() => goToStep(3)} // Updated from 4 to 3
                  style={{
                    color: '#2563eb',
                    fontSize: '0.875rem',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                >
                  Edit
                </button>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', borderBottom: '1px solid #e5e7eb', paddingBottom: '0.75rem' }}>
                <div>
                  <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0 0 0.25rem 0' }}>
                    Last known year
                  </p>
                  <p style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                    {formData.isAlive === 'yes'
                      ? 'Alive'
                      : formData.deathYear || formData.deathDecade}
                  </p>
                </div>
                <button
                  onClick={() => goToStep(4)} // Updated from 5 to 4
                  style={{
                    color: '#2563eb',
                    fontSize: '0.875rem',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                >
                  Edit
                </button>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', borderBottom: '1px solid #e5e7eb', paddingBottom: '0.75rem' }}>
                <div>
                  <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0 0 0.25rem 0' }}>Last known place</p>
                  <p style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                    {formData.deathPlace}
                  </p>
                </div>
                <button
                  onClick={() => goToStep(5)} // Updated from 6 to 5
                  style={{
                    color: '#2563eb',
                    fontSize: '0.875rem',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                >
                  Edit
                </button>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', borderBottom: '1px solid #e5e7eb', paddingBottom: '0.75rem' }}>
                <div>
                  <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0 0 0.25rem 0' }}>Most Known for</p>
                  <p style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                    {formData.profession}
                  </p>
                </div>
                <button
                  onClick={() => goToStep(6)} // Updated from 7 to 6
                  style={{
                    color: '#2563eb',
                    fontSize: '0.875rem',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                >
                  Edit
                </button>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div>
                  <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0 0 0.25rem 0' }}>Denomination</p>
                  <p style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                    {formData.classification}
                  </p>
                </div>
                <button
                  onClick={() => goToStep(7)} // Updated from 8 to 7
                  style={{
                    color: '#2563eb',
                    fontSize: '0.875rem',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                >
                  Edit
                </button>
              </div>
              {/* Image Preview in Review
              src={"uploads/character-images/" + formData.characterImage} 
              src={"http://localhost/sepharad2025/add-character/uploads/character-images/" + formData.characterImage} 
              */}
              {formData.characterImage && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', borderBottom: '1px solid #e5e7eb', paddingBottom: '0.75rem' }}>
                  <div>
                    <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0 0 0.25rem 0' }}>Character Image</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <img 
                        src={"uploads/character-images/" + formData.characterImage} 
                        alt="Character" 
                        style={{ maxWidth: '260px', borderRadius: '0.375rem', objectFit: 'cover' }}
                      />
                    </div>
                  </div>
                  <button
                    onClick={() => goToStep(8)} // Updated from 1 to 8
                    style={{
                      color: '#2563eb',
                      fontSize: '0.875rem',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      textDecoration: 'underline'
                    }}
                  >
                    Edit
                  </button>
                </div>
              )}
            </div>

            <div style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '0.5rem', padding: '1rem' }}>
              <p style={{ fontSize: '0.875rem', color: '#1e40af', margin: 0 }}>
                Please review all information carefully before submitting. You can edit any field by clicking the "Edit" button next to it.
              </p>
            </div>
          </div>
        );

      case 10: // Finish
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0' }}>
              <UserRoundCheck style={{ width: '1.5rem', height: '1.5rem', color: '#2563eb' }} />
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>The character has been created successfully</h2>             
            </div>
            <div style={{ paddingTop: 0, marginTop: 0 }}>
              <BookmarkCheck style={{ width: '8rem', height: '8rem', color: '#02be02ff', margin: '0 auto' }} />
              <p>Thank you for submitting your character information, you can now add a migration path for this character.</p>
              <button
                onClick={beginMigrationPath}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1.5rem',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  borderRadius: '0.5rem',
                  fontWeight: '500',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s',
                  margin: '50px auto 40px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#1d4ed8'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#2563eb'}
              >Add migration path
                <Map style={{ width: '1rem', height: '1rem' }} />
              </button>
              {!wpUsername && (<p className="pleasenote"><strong>Please note:</strong> If you are NOT currently registered as a member, your character will be saved only for this session and you will not be able to edit it later. To save and edit your characters anytime, please <a href="/register/" target="_blank" rel="noreferrer">sign up</a> as a member.</p>)}
            </div>
          </div>
        );
      
      case 11: // FIRST RECORDED MOVE SCREEN 1 (Title and Description)
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <User style={{ width: '1.5rem', height: '1.5rem', color: '#2563eb' }} />
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>{ordering} recorded move of {formData.firstName} {formData.lastName} {chNom}</h2>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '1.1rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                Title *
              </label>
              <p style={{ color: '#666666', fontSize: '0.875rem', marginBottom: '1rem' }}>Enter a short, clear name for this migration move. This can be based on places, a time period, or a defining event. The title helps identify this movement within the overall migration path.</p>
              <input
                type="text"
                value={formData.frmtitle}
                onChange={(e) => updateFormData({ frmtitle: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.5rem 1rem',
                  border: errors.frmtitle ? '1px solid #ef4444' : '1px solid #8ba0bf',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'border-color 0.15s'
                }}
                placeholder="Enter a name for this migration move"
                onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                onBlur={(e) => e.target.style.borderColor = errors.frmtitle ? '#ef4444' : '#8ba0bf'}
              />
              {errors.frmtitle && (
                <p style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem' }}>{errors.frmtitle}</p>
              )}
            </div>  

            <div>
              <label style={{ display: 'block', fontSize: '1.1rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                Description *
              </label>
              <p className='mpdesc' style={{ color: '#666666', fontSize: '0.875rem', marginBottom: '1rem' }}>Write between 70 and 150 words explaining the reasons and context for this migration. You may include historical background, cultural or social factors, reasons for leaving, reasons for choosing the new destination, and any significant changes linked to this move. Approximate or contextual information is acceptable. <strong>Please include all known years and chronological details related to the migration path, especially the dates and duration of the migration whenever available.</strong></p>
              <textarea maxLength="1000"
                type="text"
                value={formData.frmdesc}
                onChange={(e) => updateFormData({ frmdesc: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.5rem 1rem',
                  border: errors.frmdesc ? '1px solid #ef4444' : '1px solid #8ba0bf',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  outline: 'none'
                }}
                rows="5"
                placeholder="Enter this first recorded move description"
                onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                onBlur={(e) => e.target.style.borderColor = errors.frmdesc     ? '#ef4444' : '#8ba0bf'}
              />
              {errors.frmdesc && (
                <p style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem' }}>{errors.frmdesc}</p>
              )}
            </div>
          </div>
        );
      
      case 12: // Image Upload MP
        return <ImageUploadStep formData={formData} updateFormData={updateFormData} nextStep={nextStep} fields={{ characterImage: 'characterImageMP', originalImage: 'originalImageMP', cropData: 'cropDataMP' }} onCanProceedChange={setCanProceedFromImageStepMP} />;

      case 13: // FROM
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0' }}>
              <Map style={{ width: '1.5rem', height: '1.5rem', color: '#2563eb' }} />
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>From</h2>             
            </div>
            
            {/* Add this button at the beginning of step 12 */}
            {formData.birthPlace && formData.birthPlaceCoords && ordering === "First" && (
              <div style={{ marginBottom: '1rem' }}>
                <button
                  type="button"
                  onClick={() => {
                    updateFormData({
                      mpPlaceFrom: formData.birthPlace,
                      mpPlaceFromCoords: formData.birthPlaceCoords
                    });
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 1rem',
                    backgroundColor: '#f0f9ff',
                    color: '#0369a1',
                    border: '1px solid #bae6fd',
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                    fontWeight: '500',
                    transition: 'all 0.15s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#e0f2fe';
                    e.target.style.borderColor = '#7dd3fc';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#f0f9ff';
                    e.target.style.borderColor = '#bae6fd';
                  }}
                >
                  <MapPin style={{ width: '1rem', height: '1rem' }} />
                  Use birth location: {formData.birthPlace}
                </button>
                <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
                  Click to automatically populate with the birth location from step 3.
                </p>
              </div>
            )}
            
            <PlaceSelector 
              formData={formData}
              updateFormData={updateFormData}
              placeField="mpPlaceFrom"
              coordsField="mpPlaceFromCoords"
              error={errors.mpPlaceFrom}
            />
          </div>
        );
      
      case 14: // TO
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0' }}>
              <Map style={{ width: '1.5rem', height: '1.5rem', color: '#2563eb' }} />
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>To</h2>             
            </div>
            <PlaceSelector 
              formData={formData}
              updateFormData={updateFormData}
              placeField="mpPlaceTo"
              coordsField="mpPlaceToCoords"
              error={errors.mpPlaceTo}
            />
          </div>
        );

      case 15: // Added
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0' }}>
              <UserRoundCheck style={{ width: '1.5rem', height: '1.5rem', color: '#2563eb' }} />
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>The migration path has been added successfully</h2>             
            </div>
            <div style={{ paddingTop: 0, marginTop: 0 }}>
              <BookmarkCheck style={{ width: '8rem', height: '8rem', color: '#02be02ff', margin: '0 auto' }} />
              <button
                onClick={beginMigrationPath}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1.5rem',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  borderRadius: '0.5rem',
                  fontWeight: '500',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s',
                  margin: '50px auto 20px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#1d4ed8'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#2563eb'}
              >Add a new move
                <Map style={{ width: '1rem', height: '1rem' }} />
              </button>
              <button
                onClick={fetchPhpData}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1.5rem',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  borderRadius: '0.5rem',
                  fontWeight: '500',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s',
                  margin: '40px auto 50px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#1d4ed8'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#2563eb'}
              >See Migration Path
              </button>
            </div>
          </div>
        );
      
      case 16: // Finish
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0' }}>
              <UserRoundCheck style={{ width: '1.5rem', height: '1.5rem', color: '#2563eb' }} />
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>Migration paths of {formData.firstName} {formData.lastName} {chName}</h2>             
            </div>
            <div style={{ paddingTop: 0, display: 'none', marginTop: 0 }}>
              {loadingPhpData && <p>Loading...</p>}

              {phpError && (
                <p style={{ color: '#ef4444' }}>
                  Error: {phpError}
                </p>
              )}

              {phpData.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  {phpData.map((move, index) => (
                    <div
                      key={move.id ?? index}
                      style={{
                        padding: '1rem',
                        marginBottom: '0.75rem',
                        background: '#f9fafb',
                        border: '1px solid #e5e7eb',
                        borderRadius: '0.5rem'
                      }}
                    >
                      <p>
                        <strong>Title:</strong> {move.title}
                      </p>
                      <p>
                        <strong>Description:</strong> {move.description}
                      </p>
                      <p>
                        <strong>From:</strong> {move.from}
                      </p>
                      <p>
                        <strong>To:</strong> {move.to}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {phpData.length > 1 && (<p>You can change the order of the migration paths by dragging and dropping the panels shown below.</p>)}
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="migrationPaths">
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    style={{ marginTop: "1rem" }}
                  >
                    {moves.map((move, index) => (
                      <Draggable
                        key={move.id}
                        draggableId={String(move.id)}
                        index={index}
                      >
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={{
                              padding: "1rem",
                              marginBottom: "0.75rem",
                              border: "1px solid #e5e7eb",
                              borderRadius: "0.5rem",
                              ...provided.draggableProps.style
                            }}
                          >
                            <p><strong>Title:</strong> {move.title}</p>
                            <p><strong>Description:</strong> {move.description}</p>
                            <p><strong>From:</strong> {move.from}</p>
                            <p><strong>To:</strong> {move.to}</p>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <button
                onClick={beginMigrationPath}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1.5rem',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  borderRadius: '0.5rem',
                  fontWeight: '500',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s',
                  margin: '50px auto 20px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#1d4ed8'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#2563eb'}
              >Add New Migration Path
                <Map style={{ width: '1rem', height: '1rem' }} />
              </button>
              <button
                onClick={openNewChar}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1.5rem',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  borderRadius: '0.5rem',
                  fontWeight: '500',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s',
                  margin: '40px auto 50px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#1d4ed8'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#2563eb'}
              >Add new character
              </button>
              <button
                onClick={openFinsh}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1.5rem',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  borderRadius: '0.5rem',
                  fontWeight: '500',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s',
                  margin: '40px auto 50px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#1d4ed8'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#2563eb'}
              >Finish
              </button>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  const getProgress = () => {
    if(step < 11){
      return (step / 10) * 100;
    }else{
      return ((step-10) / 5) * 100;
    }
  };

  let paStep = "Step " + step + " of 10"; 

  if(step > 10 ){
    paStep = "Step " + (step-10) + " of 5"
  }

  return (
    <div className="divprin">
      <div className="logoheader"><a href="/"><img src="/addchar/logoSephardiSpaxces.png" alt="Sephardi Spaces" /></a></div>
      <div className="elh1"><h1>Add a Character</h1></div>
      <div style={{ maxWidth: '42rem', margin: '0 auto' }}>
        {/* Progress Bar */}
        <div style={{ marginBottom: '2rem' }}>
          <div className="progsteps" style={{ display: step === 16 ? 'none' : 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#4b5563', marginBottom: '0.5rem' }}>
            <span>{paStep}</span>
            <span>{Math.round(getProgress())}% Complete</span>
          </div>
          <div style={{ height: '0.5rem', backgroundColor: step === 16 ? 'unset' : '#e5e7eb', borderRadius: '9999px', overflow: 'hidden' }}>
            <div
              style={{
                height: '100%',
                backgroundColor: '#2563eb',
                transition: 'width 0.3s',
                width: `${getProgress()}%`,
                display: step === 16 ? 'none' : 'block',
              }}
            ></div>
          </div>
        </div>

        {/* Main Card */}
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          borderRadius: '0.75rem',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          padding: '2rem'
        }}>
          {renderStep()}

          {/* Navigation Buttons */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '2rem',
            paddingTop: '1.5rem',
            borderTop: '1px solid #e5e7eb'
          }}>
            {(step < 10) || (step >= 11 && step <= 14) ? (
            <button
              onClick={prevStep}
              disabled={step === 1 || step === 11}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1.5rem',
                borderRadius: '0.5rem',
                fontWeight: '500',
                backgroundColor: step === 1 || step === 11 ? '#f3f4f6' : '#e5e7eb',
                color: step === 1 || step === 11 ? '#9ca3af' : '#374151',
                border: 'none',
                cursor: step === 1 || step === 11 ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.15s'
              }}
              onMouseEnter={(e) => {
                if (step !== 1 || step !== 11 ) e.target.style.backgroundColor = '#8ba0bf';
              }}
              onMouseLeave={(e) => {
                if (step !== 1) e.target.style.backgroundColor = '#e5e7eb';
              }}
            >
              <ChevronLeft style={{ width: '1rem', height: '1rem' }} />
              Back
            </button>
            ):null}

            {step === 9 ? (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1.5rem',
                  backgroundColor: isSubmitting ? '#9ca3af' : '#16a34a',
                  color: 'white',
                  borderRadius: '0.5rem',
                  fontWeight: '500',
                  border: 'none',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.15s'
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) e.target.style.backgroundColor = '#15803d';
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) e.target.style.backgroundColor = '#16a34a';
                }}
              >
                {isSubmitting ? (
                  <>
                    <div style={{
                      width: '1rem',
                      height: '1rem',
                      border: '2px solid white',
                      borderTopColor: 'transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <Check style={{ width: '1rem', height: '1rem' }} />
                    Submit
                  </>
                )}
              </button>
            ) : step === 14 ? (
              <button
                onClick={handleSubmitFRM}
                disabled={isSubmitting}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1.5rem',
                  backgroundColor: isSubmitting ? '#9ca3af' : '#2563eb',
                  color: 'white',
                  borderRadius: '0.5rem',
                  fontWeight: '500',
                  border: 'none',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.15s'
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) e.target.style.backgroundColor = '#1d4ed8';
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) e.target.style.backgroundColor = '#2563eb';
                }}
              >
                {isSubmitting ? (
                  <>
                    <div style={{
                      width: '1rem',
                      height: '1rem',
                      border: '2px solid white',
                      borderTopColor: 'transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <Check style={{ width: '1rem', height: '1rem' }} />
                    Submit
                  </>
                )}
              </button>
            ) : step === 10 ? null : (step >= 15) ? null : (
              <button
                onClick={nextStep}
                disabled={!canGoNext()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1.5rem',
                  backgroundColor: canGoNext() ? '#2563eb' : '#9ca3af',
                  color: 'white',
                  borderRadius: '0.5rem',
                  fontWeight: '500',
                  border: 'none',
                  cursor: canGoNext() ? 'pointer' : 'not-allowed',
                  transition: 'background-color 0.15s'
                }}
                onMouseEnter={(e) => {
                  if (canGoNext()) e.target.style.backgroundColor = '#1d4ed8';
                }}
                onMouseLeave={(e) => {
                  if (canGoNext()) e.target.style.backgroundColor = canGoNext() ? '#2563eb' : '#9ca3af';
                }}
              >
                {step === 8 ? 'Confirm & Continue' : 'Next'}
                <ChevronRight style={{ width: '1rem', height: '1rem' }} />
              </button>
            )}
          </div>
        </div>
        {/* Help Text */}
        <div style={{
          marginTop: '1rem',
          textAlign: 'center',
          fontSize: '0.875rem',
          color: '#333333'
        }}>
          All fields marked with * are required
        </div>
      </div>

      {/* Add keyframe animation for spinner */}
      <style>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
    
  );
};

export default CharacterRegistration;